import React, { useState, useEffect } from 'react';
import { Button, Form, Table, Modal, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { AiOutlinePlus, AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import { FaGraduationCap } from 'react-icons/fa';
import axios from '../utils/api';
import RichTextEditor from './RichTextEditor';
import './AdminDashboard.css';

function EducationEditor() {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    degree: '',
    field_of_study: '',
    institution: '',
    location: '',
    start_date: '',
    end_date: '',
    grade_gpa: '',
    description: '',
    institution_website: '',
    certificate_url: ''
  });

  useEffect(() => {
    fetchEducation();
  }, []);

  const fetchEducation = async () => {
    try {
      const response = await axios.get('/content/experience-education?type=education');
      // Sort by: NULL end_date first (in-progress), then end_date DESC, then start_date DESC
      const sorted = (response.data.data || []).sort((a, b) => {
        const aIsInProgress = !a.end_date;
        const bIsInProgress = !b.end_date;
        
        if (aIsInProgress && !bIsInProgress) return -1;
        if (!aIsInProgress && bIsInProgress) return 1;
        
        if (!aIsInProgress && !bIsInProgress) {
          const endDateA = new Date(a.end_date);
          const endDateB = new Date(b.end_date);
          const endDateDiff = endDateB - endDateA;
          if (endDateDiff !== 0) return endDateDiff;
        }
        
        const startDateA = new Date(a.start_date);
        const startDateB = new Date(b.start_date);
        return startDateB - startDateA;
      });
      setEducation(sorted);
    } catch (err) {
      setError('Failed to load education');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        degree: entry.degree || '',
        field_of_study: entry.field_of_study || '',
        institution: entry.institution || '',
        location: entry.location || '',
        start_date: entry.start_date ? entry.start_date.split('T')[0] : '',
        end_date: entry.end_date ? entry.end_date.split('T')[0] : '',
        grade_gpa: entry.grade_gpa || '',
        description: entry.description || '',
        institution_website: entry.institution_website || '',
        certificate_url: entry.certificate_url || ''
      });
    } else {
      setEditingEntry(null);
      setFormData({
        degree: '',
        field_of_study: '',
        institution: '',
        location: '',
        start_date: '',
        end_date: '',
        grade_gpa: '',
        description: '',
        institution_website: '',
        certificate_url: ''
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEntry(null);
    setFormData({
      degree: '',
      field_of_study: '',
      institution: '',
      location: '',
      start_date: '',
      end_date: '',
      grade_gpa: '',
      description: '',
      institution_website: '',
      certificate_url: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.degree || !formData.institution || !formData.start_date) {
      setError('Degree, institution, and start date are required');
      return;
    }

    try {
      if (editingEntry) {
        await axios.put('/content/experience-education?type=education', {
          id: editingEntry.id,
          ...formData
        });
        setSuccess('Education entry updated successfully');
      } else {
        await axios.post('/content/experience-education?type=education', formData);
        setSuccess('Education entry added successfully');
      }
      
      handleCloseModal();
      fetchEducation();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save education entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this education entry?')) {
      return;
    }

    try {
      await axios.delete('/content/experience-education?type=education', { data: { id } });
      setSuccess('Education entry deleted successfully');
      fetchEducation();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete education entry');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading education...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="editor-card">
      {error && <Alert variant="danger" className="alert-admin" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" className="alert-admin" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="editor-header">
        <h3>
          <FaGraduationCap style={{ marginRight: '0.5rem', color: '#667eea' }} />
          Manage Education & Qualifications
        </h3>
        <Button className="btn-primary-admin" onClick={() => handleOpenModal()}>
          <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
          Add New Entry
        </Button>
      </div>

      {education.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎓</div>
          <h4>No education entries yet</h4>
          <p>Start building your academic profile!</p>
          <Button className="btn-primary-admin mt-3" onClick={() => handleOpenModal()}>
            <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
            Add Your First Education Entry
          </Button>
        </div>
      ) : (
        <Table className="admin-table" responsive>
          <thead>
            <tr>
              <th>Degree</th>
              <th>Field of Study</th>
              <th>Institution</th>
              <th>Location</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {education.map((edu) => (
              <tr key={edu.id}>
                <td><strong>{edu.degree}</strong></td>
                <td>{edu.field_of_study || '-'}</td>
                <td>
                  {edu.institution_website ? (
                    <a href={edu.institution_website} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea', textDecoration: 'none' }}>
                      {edu.institution} 🔗
                    </a>
                  ) : (
                    edu.institution
                  )}
                </td>
                <td>{edu.location || '-'}</td>
                <td>
                  {formatDate(edu.start_date)} - {edu.end_date ? formatDate(edu.end_date) : 'Present'}
                </td>
                <td>
                  {!edu.end_date ? (
                    <span style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      In Progress
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>Completed</span>
                  )}
                </td>
                <td>
                  <Button 
                    className="btn-outline-primary-admin me-2" 
                    size="sm"
                    onClick={() => handleOpenModal(edu)}
                  >
                    <AiOutlineEdit style={{ marginRight: '0.25rem' }} />
                    Edit
                  </Button>
                  <Button 
                    className="btn-danger-admin" 
                    size="sm"
                    onClick={() => handleDelete(edu.id)}
                  >
                    <AiOutlineDelete style={{ marginRight: '0.25rem' }} />
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg" className="admin-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingEntry ? '✏️ Edit Education Entry' : '➕ Add Education Entry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} className="admin-form">
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Degree *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.degree}
                    onChange={(e) => setFormData({ ...formData, degree: e.target.value })}
                    placeholder="e.g., Bachelor of Science"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Field of Study</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.field_of_study}
                    onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
                    placeholder="e.g., Computer Systems Engineering"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Institution *</Form.Label>
              <Form.Control
                type="text"
                value={formData.institution}
                onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                placeholder="e.g., University of Sousse"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Sousse, Tunisia"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Grade/GPA</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.grade_gpa}
                    onChange={(e) => setFormData({ ...formData, grade_gpa: e.target.value })}
                    placeholder="e.g., 3.8/4.0, A+, First Class"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date}
                  />
                  <Form.Text className="text-muted">
                    Leave empty if still in progress
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Institution Website</Form.Label>
              <Form.Control
                type="url"
                value={formData.institution_website}
                onChange={(e) => setFormData({ ...formData, institution_website: e.target.value })}
                placeholder="https://www.university.edu"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                placeholder="Describe your coursework, achievements, projects, honors, etc..."
                rows={4}
              />
              <Form.Text className="text-muted">Use the toolbar above to format your description (Bold, Italic, Lists, etc.).</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Certificate URL</Form.Label>
              <Form.Control
                type="url"
                value={formData.certificate_url}
                onChange={(e) => setFormData({ ...formData, certificate_url: e.target.value })}
                placeholder="https://example.com/certificate.pdf"
              />
              <Form.Text className="text-muted">
                Link to your diploma/certificate (PDF, image, or webpage)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button className="btn-primary-admin" type="submit">
              {editingEntry ? '💾 Update Entry' : '✨ Add Entry'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default EducationEditor;

